import React, { useEffect, useState } from "react";
import { AxButton, AxSpinner } from "@dynata/axiom3";
import { useTransactionReportDownload } from "../hooks/transaction/useTransactionReportDownload.tsx";
import { useStateContext } from "state";
import {
  convertMonthNameToNumeric,
  convertNumericMonthToName,
  getPendingReports,
  removePendingReport,
  savePendingReport,
} from "core/utils/transaction.utils";
import { MdOutlineFileDownload } from "react-icons/md";
import "scss/manage-users.scss";
import { useReconciliationReportDownload } from "../hooks/reconciliation/useReconciliationReportDownload.tsx";

export interface Transaction {
  id: string | null;
  currentMonth: boolean;
  month: string;
  year: number;
  status: string;
  created_at: string;
}

interface TransactionsTableProps {
  transactions: Transaction[];
  setTransactions: React.Dispatch<React.SetStateAction<Transaction[]>>;
  reportType: string;
  isTableLoading: boolean;
  accountId: string;
}

const reconciliationType = "reconciliations";

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  transactions,
  reportType,
  isTableLoading,
  accountId,
}) => {
  const { mutateAsync: generateReport } = useTransactionReportDownload();
  const { mutateAsync: generateReconciliationReport } =
    useReconciliationReportDownload();

  const [loadingTransactions, setLoadingTransactions] = useState<Set<string>>(
    new Set(),
  );
  const [lastCreatedAtDay, setLastCreatedAtDay] = useState<string>();
  const state = useStateContext();

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;

  const iconStyle = {
    fontSize: "18px",
  };

  const buttonStyle = {
    minWidth: "124px",
    paddingTop: "8px",
  };

  const handleDownload = async (transaction: Transaction) => {
    if (!state.user) return;

    const { year, month, id: existingReportId, created_at } = transaction;
    const monthNumeric = convertMonthNameToNumeric(month);
    if (monthNumeric === null) {
      console.error("Invalid month name:", month);
      return;
    }

    const lastDayOfMonth = new Date(year, monthNumeric, 0);
    const refreshReport = created_at
      ? new Date(created_at) <= lastDayOfMonth
      : false;
    const account = accountId!.toString();

    setLoadingTransactions((prev) => new Set(prev).add(month));

    try {
      savePendingReport(account, year, reportType, monthNumeric);
      if (reportType === reconciliationType) {
        await generateReconciliationReport({
          account,
          year,
          report_type: reportType,
          month: monthNumeric,
          id: refreshReport ? "" : existingReportId || "",
        });
      } else {
        await generateReport({
          account,
          year,
          report_type: reportType,
          month: monthNumeric,
          id: refreshReport ? "" : existingReportId || "",
        });
      }
    } catch (error) {
      console.error(`Failed to generate report for ${month}:`, error);
    } finally {
      removePendingReport(year, monthNumeric);
      setLoadingTransactions((prev) => {
        const updatedSet = new Set(prev);
        updatedSet.delete(month);
        return updatedSet;
      });
    }
  };

  useEffect(() => {
    if (transactions.length !== 0) {
      const date = new Date(`${transactions[0].created_at}Z`);

      const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setLastCreatedAtDay(
        date.toLocaleString(undefined, {
          weekday: "long",
          month: "long",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          timeZone: localTimeZone,
        }),
      );
    }

    const pendingReports = getPendingReports();

    if (
      !Array.isArray(pendingReports) ||
      pendingReports.length === 0 ||
      transactions.length === 0
    )
      return;

    setLoadingTransactions(
      new Set(pendingReports.map((r) => convertNumericMonthToName(r.month))),
    );

    pendingReports.forEach(async ({ account, year, reportType, month }) => {
      const transaction = transactions.find(
        (t) => t.year === year && t.month === month,
      );
      const transactionId = transaction?.id || undefined;

      try {
        if (reportType === reconciliationType) {
          await generateReconciliationReport({
            account,
            year,
            report_type: reportType,
            month,
            id: transactionId,
          });
        } else {
          await generateReport({
            account,
            year,
            report_type: reportType,
            month,
            id: transactionId,
          });
        }
      } catch (error) {
        console.error(`Error generating report for ${month}:`, error);
      } finally {
        setLoadingTransactions((prev) => {
          const updatedSet = new Set(prev);
          updatedSet.delete(convertNumericMonthToName(month));
          return updatedSet;
        });
        removePendingReport(year, month);
      }
    });
  }, [transactions]);

  return (
    <table className="manageUsers_table">
      <thead>
        <tr>
          <th>Month</th>
          <th>Year</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {isTableLoading ? (
          <tr>
            <td
              colSpan={3}
              style={{ textAlign: "center", padding: "6rem" }}
            >
              <AxSpinner size="medium" />
              <p>Loading transactions...</p>
            </td>
          </tr>
        ) : (
          transactions.map((transaction) => {
            const isLoading = loadingTransactions.has(transaction.month);
            return (
              <tr key={transaction.month}>
                <td>
                  {transaction.month}
                  {transaction.currentMonth ? (
                    transaction.status === "ungenerated" ? (
                      ""
                    ) : (
                      <span style={{ fontStyle: "italic" }}>
                        &nbsp;(Last downloaded on:{" "}
                        <strong>{lastCreatedAtDay}</strong>)
                      </span>
                    )
                  ) : null}
                </td>
                <td>{transaction.year}</td>
                <td>
                  {isLoading ? (
                    <AxButton
                      type="primary"
                      color="positive"
                      disabled
                      style={buttonStyle}
                    >
                      <AxSpinner size="small" />
                    </AxButton>
                  ) : (
                    <AxButton
                      type="primary"
                      color="positive"
                      onClick={() => handleDownload(transaction)}
                    >
                      Download <MdOutlineFileDownload style={iconStyle} />
                    </AxButton>
                  )}
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
};

export default React.memo(TransactionsTable);
